import React from 'react';
import { css } from '@emotion/core';
import _ from 'underscore';
import moment from 'moment';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import t from 'react-translate';
import validationConstants from 'shared/services/constants-shared';

// schemas
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { Course } from 'redux/schemas/models/course';
import { RootState } from 'redux/schemas';

// actions
import { addAlertMessage } from 'redux/actions/alert-messages';
import { getCourseClonings } from 'redux/actions/institutions';
import { doRequestCloneCourse } from 'redux/actions/license';

// components
import NvDatepicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvRadioButton from 'shared/components/inputs/nv-radio-button';

// styles
import { standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { useAppDispatch } from 'redux/store';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import { CloneCourseRequest } from 'redux/schemas/api/license';

import { config } from '../../../config/config.json';

type StateProps = {
  currentInstitutionId: number,
};

type OwnProps = {
  course: Course,
  closeModal(): void,
};

const actions = {
  addAlertMessage,
  getCourseClonings,
};

export const CourseCloneModal = (props: StateProps & typeof actions & OwnProps) => {
  const validationSchema = yup.object().shape({
    catalogId: yup.string()
      .required(t.VALIDATION.REQUIRED())
      .matches(validationConstants.CATALOG_ID_REGEX, {
        message: t.COURSES.FORM.VALIDATION_TEXT.CATALOG_ID_PATTERN(),
      }),
    startDate: yup.string()
      .required(t.VALIDATION.REQUIRED()),
  });

  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      catalogId: '',
      startDate: null,
      copyAudioVideo: '',
      cohort: props.course.isPrimary ? 'true' : '',
    },
    resolver: yupResolver(validationSchema),
  });
  const { handleSubmit, formState } = methods;

  /** Responds to the form submission response in Reux */
  const handleSubmitResponse = () => {
    props.addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.COURSES.CLONE.SUCCESS_HEADER(),
      message: t.COURSES.CLONE.SUCCESS_BODY(),
    });

    // Request the current clonings list make the table redraw with the 'Cloning in progress...' row
    props.getCourseClonings({ institutionId: props.currentInstitutionId });

    props.closeModal();
  };

  const dispatch = useAppDispatch();

  /** Submits a course cloning request, sending a callback to update local UI state */
  const onSubmit = ({ catalogId, startDate, copyAudioVideo, cohort }) => {
    const payload: CloneCourseRequest = {
      id: props.course.id,
      newCatalogId: catalogId,
      newReleaseDate: moment(startDate, 'L LT').toISOString(),
      copyAudioVideo: !!copyAudioVideo,
      callback: null,
    };

    if (cohort) {
      payload.createCohort = true;
    }

    dispatch(doRequestCloneCourse(payload)).then(result => {
      if (result.payload) {
        handleSubmitResponse();
      }
    });
  };

  const styles = css`
    padding: 0 ${standardSpacing}px;

    .cohort-checkbox {
      margin-bottom: ${threeQuartersSpacing}px;
    }

    .clone-form-container {
      margin: 0 auto;
      max-width: 400px;

      .catalog-id-description {
        height: ${standardSpacing}px;
      }

      .submit-button {
        margin: 0 auto;
        margin-top: ${standardSpacing * 3}px;
        margin-bottom: ${standardSpacing}px;
      }

      .popover-wrapper {
        width: 100%;
      }

      .start-date {
        width: 100%;
      }
    }
  `;

  return (
    <div css={styles} className='clone-course-modal'>
      {/* eslint-disable-next-line react/no-danger */}
      <p className='mb-4'>{ t.COURSES.CLONE.DESCRIPTION(props.course.catalogId, props.course.name, props.course.isPrimary.toString()) }</p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='form-container'>
            {props.course.isPrimary && (
              <React.Fragment>
                <NvRadioButton
                  withForm
                  value='true'
                  name='cohort'
                  className='cohort-checkbox'
                  label={t.COURSES.CLONE.NEW_COHORT_COURSE()}
                />
                <NvRadioButton
                  withForm
                  value=''
                  name='cohort'
                  className='cohort-checkbox'
                  label={t.COURSES.CLONE.NEW_PRIMARY_COURSE()}
                />
              </React.Fragment>
            )}
            <div className='clone-form-container'>
              <label htmlFor='catalog-id'>{t.COURSES.CLONE.CATALOG_ID()}</label>
              <div className='bs4-row'>
                <NvTextInput
                  withForm
                  autoComplete='off'
                  name='catalogId'
                  ariaLabel='Catalog ID'
                  className='bs4-col'
                  required
                  data-qa={config.pendo.cloningModal.catalogId}
                />
              </div>
              <div className='catalog-id-description text-small text-gray-2 mt-1 mb-1'>
                {t.COURSES.FORM.HELP_TEXT.CATALOG_ID()}
              </div>

              <label htmlFor='start-date bs4-row'>{t.COURSES.CLONE.START_DATE()}</label>
              <div className='bs4-row'>
                <div className='bs4-col'>
                  <NvDatepicker
                    withForm
                    required
                    fixed
                    placement='bottom'
                    name='startDate'
                    type={DatePickerType.DATETIME}
                    className='start-date'
                    placeholder={t.FORM.SELECT_DATE_TIME()}
                    dataQa={config.pendo.cloningModal.startDate}
                  />
                </div>
              </div>

              <div className='font-weight-bold mb-2 mt-3'>{t.COURSES.CLONE.COPY_SHARE_HEADING()}</div>

              <NvRadioButton
                withForm
                value=''
                name='copyAudioVideo'
                label={t.COURSES.CLONE.SHARE_AUDIO_AND_VIDEO.LABEL()}
              />
              <div className='text-small text-gray-2 pl-5 mb-2'>
                {t.COURSES.CLONE.SHARE_AUDIO_AND_VIDEO.DESCRIPTION()}
              </div>

              <NvRadioButton
                withForm
                value='true'
                name='copyAudioVideo'
                label={t.COURSES.CLONE.COPY_AUDIO_AND_VIDEO.LABEL()}
              />
              <div className='text-small text-gray-2 pl-5'>
                {t.COURSES.CLONE.COPY_AUDIO_AND_VIDEO.DESCRIPTION()}
              </div>

              <div className='bs4-row'>
                <Button
                  className='submit-button'
                  disabled={!formState.isValid}
                  type='submit'
                  data-qa={config.pendo.cloningModal.create}
                >
                  {t.FORM.CREATE()}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default connect<StateProps, typeof actions, OwnProps, RootState>((state: RootState, ownProps) => ({
  currentInstitutionId: state.app.currentInstitutionId,
  ...ownProps,
}), actions as any)(CourseCloneModal);
